import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Form, Button, Row, Col, Alert, Toast, ToastContainer } from "react-bootstrap";
import { useCommonData } from "@/hooks/useCommonData";
import { FaultLog, GetFaultLogCombinedComments } from "@/shared/models/FaultLog";
import { DateTimeHelper } from "@/shared/utils/DateTime.helper";
import { setToastSuccess } from "@/redux/shareSlice";
import {
  FaultLogPriorityList,
  FaultLogStatusList,
  FaultLogTelemetrySystemList,
  FaultLogLogisticsImpactList,
  FilterOperator,
  FaultLogStatus,
  FaultLogOwnerList,
  FaultLogCustomerFlagList,
  DataType,
} from "@/shared/Enums";
import FormGroupSelect from "@/components/FormGroupSelect";
import { GetTerminalDisplayName } from "@/shared/models/Terminal";
import FormGroupNumber from "@/components/FormGroupNumber";
import { FaultLogApi } from "@/services/FaultLog.api";
import { IDashboardState } from "@/redux/dashboardSlice";
import { FaultLogComment } from "@/shared/models/FaultLogComment";
import "./style.css";

import FormGroupMultiInput from "@/components/FormGroupMultiInput";
import { isAdmin, isEditor } from "@/shared/utils/roles";
import { useAuth } from "@/context/AuthContext";
import SectionBorder from "@/components/SectionBorder";
import EditableTable from "@/components/EditableTable";
import DropdownInside from "@/components/DropdownInside";
import ApModalDialog from "@/components/ApModalDialog";
import { CustomerRow, DeviceRow } from "@/shared/models/FaultLogRows";
import {
  apiToCustomerRows,
  apiToDeviceRows,
  customerRowsToApi,
  deviceRowsToApi,
  validateCustomerRows,
  validateDeviceRows,
} from "@/shared/utils/faultLogTransformers";
import { createEmptyCustomerRow, createEmptyDeviceRow } from "@/shared/models/FaultLogRows";
import DevicePicList from "@/components/DevicePicList";
import { store } from "@/redux/store";
import { Utils } from "@/shared/utils/Utils";
import { BusinessHelper } from "@/shared/utils/Business.helper";
import DropDownSelect from "@/components/DropDownSelect";
import { Constants } from "@/shared/Constants";

interface FaultLogEditFormProps {
  editItemId: number | undefined;
  setNewFaultLogId: (id: number) => void;
  onCancel: () => void;
}

const FaultLogEditForm = ({ editItemId, setNewFaultLogId, onCancel }: FaultLogEditFormProps) => {
  const { userRoles, userDomain, userName } = useAuth();
  const {
    terminals,
    products,
    countryRegionDomains,
    faultCodesCodeList,
    regionOptions,
    countryOptions,
    loadCommonData,
    getFaultCodeWorkOrderInstructions,
  } = useCommonData();
  const [data, setData] = useState<FaultLog | undefined>(undefined);
  const [currentSelectRegion, setCurrentSelectRegion] = useState("");
  const [newComments, setNewComments] = useState("");
  const [customerRows, setCustomerRows] = useState<CustomerRow[]>([]);
  const [deviceRows, setDeviceRows] = useState<DeviceRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isEngineeringNotificationSend, setIsEngineeringNotificationSend] = useState(false);
  const [isLogisticsNotificationSend, setIsLogisticsNotificationSend] = useState(false);
  // const [showEngineeringConfirm, setShowEngineeringConfirm] = useState(false);
  const [showEngineerNotifyHint, setShowEngineerNotifyHint] = useState(false);
  const [notifyHintMessage, setNotifyHintMessage] = useState("");
  // const [pendingFormData, setPendingFormData] = useState<FaultLog | null>(null);
  const isFirstSaveNewDataRef = useRef(false);
  const [isSyncingSapStatus, setIsSyncingSapStatus] = useState(false);
  const [sapSyncToast, setSapSyncToast] = useState({ show: false, message: "" });

  useEffect(() => {
    if (sapSyncToast.show) {
      const timer = setTimeout(() => setSapSyncToast({ show: false, message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [sapSyncToast.show]);

  const methods = useForm<FaultLog>();
  const { register, handleSubmit, reset, watch, getValues, setValue } = methods;

  useEffect(() => {
    if (data && BusinessHelper.IsEUHanlderSpontaneouslyRecoveredAndClosedItemFlashing(data, userDomain, userRoles)) {
      FaultLogApi.SetSpontaneouslyRecoveredViewed(data.id);
    }
  }, [data, userDomain, userRoles]);

  useEffect(() => {
    loadCommonData();
  }, [loadCommonData]);

  const loading = useCallback(
    async (id: number | undefined) => {
      if (!id) {
        setData({} as FaultLog);
        return;
      }

      var filter = {
        advancedFilters: [{ field: "id", operator: FilterOperator.Equal.toString(), value: id.toString(), type: DataType.Int }],
      } as IDashboardState;
      const faultLogDataList = await FaultLogApi.query(filter, countryRegionDomains, userDomain, userRoles, true, true);
      const faultLogData = faultLogDataList && faultLogDataList.length === 1 ? faultLogDataList[0] : undefined;

      setData(faultLogData);
      if (isFirstSaveNewDataRef.current) {
        showSaveSuccessToast("Fault created successfully.");
        isFirstSaveNewDataRef.current = false;
      }
    },
    [userRoles, userDomain, countryRegionDomains]
  );

  // Edit permission independent of open/closed status, so closed faults can still be acted on (e.g. notify) by Editor/Admin
  const canManageFaultLog = useMemo(() => {
    if (isAdmin(userRoles)) {
      return true;
    }

    return isEditor(userRoles) && (data?.domain === userDomain || !editItemId);
  }, [userRoles, data, userDomain, editItemId]);

  const isReadonly = useMemo(() => {
    if (data?.status === FaultLogStatus.Closed || data?.status === FaultLogStatus.Deleted) {
      return true;
    }

    return !canManageFaultLog;
  }, [data, canManageFaultLog]);

  const isCustomerManagedDevice = useMemo(() => {
    return BusinessHelper.IsCustomerManagedDevice(data);
  }, [data]);

  const canSendEngineeringNotification = useMemo(() => {
    return data?.status === FaultLogStatus.Open && !isReadonly;
  }, [isReadonly, data]);

  // Logistics notification must remain available after a manual close, so Editor/Admin can decide to notify
  const canSendLogisticsNotification = useMemo(() => {
    return (data?.status === FaultLogStatus.Open || data?.status === FaultLogStatus.Closed) && canManageFaultLog;
  }, [canManageFaultLog, data]);

  const isSendEngineeringNotificationDisabled = useMemo(() => {
    return (data?.sapNotificationNumber && data.sapNotificationNumber > 0) || isEngineeringNotificationSend;
  }, [data, isEngineeringNotificationSend]);

  useEffect(() => {
    loading(editItemId);
  }, [editItemId, loading]);

  useEffect(() => {
    if (data) {
      // Transform parallel arrays to row objects for editing
      const customers = apiToCustomerRows(data);
      const devices = apiToDeviceRows(data);

      setCustomerRows(customers);
      setDeviceRows(devices);

      reset({
        id: data.id || 0,
        numOfDeliveries: data.numOfDeliveries,
        acknowledged: data.acknowledged || false,
        acknowledgedBy: data.acknowledgedBy || "",
        acknowledgedTime: data.acknowledgedTime,
        assetName: data.assetName || [],
        country: data.country || "",
        customerFlag: data.customerFlag || [],
        customerName: data.customerName || [],
        customerCity: data.customerCity || [],
        customerStateCode: data.customerStateCode || [],
        customerShipTo: data.customerShipTo || [],
        customerDemandType: data.customerDemandType || [],
        customerPostCode: data.customerPostCode || [],
        deviceId: data.deviceId || [],
        deviceType: data.deviceType || [],
        devicePic: data.devicePic || [],
        faultCode: data.faultCode || "",
        combinedComments: GetFaultLogCombinedComments(data),
        status: data.status || "",
        impactOnLogistics: data.impactOnLogistics || "",
        priority: data.priority || "",
        product: data.product || "",
        terminal: data.terminal || "",
        owner: data.owner || "",
        region: data.region || "",
        reportedBy: data.reportedBy || "",
        reportedTime: data.reportedTime,
        rootCause: data.rootCause || "",
        sapNotificationNumber: data.sapNotificationNumber,
        siteVisitDate: data.siteVisitDate,
        lastCreateSapNotifyMessage: data.lastCreateSapNotifyMessage,
        telemetrySystem: data.telemetrySystem || "",
        toEngineeringDate: data.toEngineeringDate,
        vesselId: data.vesselId || [],
        workOrderNumber: data.workOrderNumber || "",
        workOrderUserStatus: data.workOrderUserStatus || "",
        workOrderInstructions: data.workOrderInstructions || "",
        workOrderSystemStatusDesc: data.workOrderSystemStatusDesc || "",
        workOrderUserStatusDesc: data.workOrderUserStatusDesc || "",
        notificationStatusDesc: data.notificationStatusDesc || "",
        workOrderLongText: data.workOrderLongText || "",
      } as any);
    }
  }, [data, reset]);

  const devicePicFileNames = useMemo(() => (data?.devicePic ?? []).filter((f) => !Utils.isEmpty(f)), [data?.devicePic]);

  const customerInfoColumns = useMemo(() => {
    const columnDefineList = [
      {
        key: "flag",
        label: "Flag",
        maxLength: 200,
        minWidth: "165px",
        placeholder: "Select flags",
        render: (value: any, rowIndex: number, onChange: (value: any) => void, isReadonly: boolean) => {
          const currentValues = value
            ? String(value)
                .split(",")
                .filter((v) => v.trim())
            : [];
          return (
            <DropdownInside
              label="Flag"
              options={FaultLogCustomerFlagList}
              value={currentValues}
              onChange={(selectedValues) => onChange(selectedValues.join(","))}
              disabled={isReadonly}
            />
          );
        },
      },
      { key: "name", label: "Customer Name", maxLength: 100, minWidth: "150px", placeholder: "Enter customer name" },
      { key: "city", label: "City", maxLength: 50, minWidth: "100px", placeholder: "Enter city" },
      { key: "postCode", label: "Post Code", maxLength: 20, minWidth: "110px", placeholder: "Enter post code" },
      {
        key: "shipTo",
        label: "Ship To",
        required: true,
        maxLength: 120,
        minWidth: "110px",
        placeholder: "Enter ship to address",
      },
      { key: "demandType", label: "Demand Type", maxLength: 50, minWidth: "110px", placeholder: "Enter demand type" },
      {
        key: "terminal",
        label: "Terminal",
        required: true,
        minWidth: "150px",
        render: (value: any, _rowIndex: number, onChange: (value: any) => void, isReadonly: boolean) => (
          <DropDownSelect
            placeholder="Select Terminal..."
            options={terminals.map((t) => ({ value: t.primaryTermicalId, label: GetTerminalDisplayName(t) }))}
            value={value || ""}
            onChange={onChange}
            isReadonly={isReadonly}
          ></DropDownSelect>
        ),
      },
      {
        key: "product",
        label: "Product",
        minWidth: "140px",
        render: (value: any, _rowIndex: number, onChange: (value: any) => void, isReadonly: boolean) => (
          <DropDownSelect
            placeholder="Select Product..."
            options={products.map((p) => ({ value: p.productClass, label: p.displayProductName || p.productClass }))}
            value={value || ""}
            onChange={onChange}
            isReadonly={isReadonly}
          ></DropDownSelect>
        ),
      },
      {
        key: "numOfDeliveries",
        label: "# of Deliveries in Last 30 Days",
        minWidth: "110px",
        render: (value: any, _rowIndex: number, onChange: (value: any) => void, isReadonly: boolean) => (
          <Form.Control
            type="number"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "." || e.key === ",") e.preventDefault();
            }}
            disabled={isReadonly}
            className="number-control-no-spinner"
            placeholder="Enter # of Deliveries in Last 30 Days"
          />
        ),
      },
      { key: "vesselId", label: "Vessel ID", maxLength: 20, minWidth: "110px", placeholder: "Enter vessel ID" },
    ];

    if ((currentSelectRegion === "" && data?.region?.startsWith("NA")) || currentSelectRegion.startsWith("NA")) {
      columnDefineList.splice(3, 0, {
        key: "stateCode",
        label: "State Code",
        maxLength: 2,
        minWidth: "100px",
        placeholder: "Enter state code",
      });
    }

    return columnDefineList as any[];
  }, [data?.region, currentSelectRegion, terminals, products]);

  const validateFaultLogData = () => {
    setValidationErrors([]);
    // Use canManageFaultLog (not isReadonly) so a closed fault's notify action isn't blocked by the closed-forces-readonly rule
    if (!canManageFaultLog) return false;

    const customerValidationErrors = validateCustomerRows(customerRows);
    const deviceValidationErrors = validateDeviceRows(deviceRows);
    const allValidationErrors = [...customerValidationErrors, ...deviceValidationErrors];

    if (allValidationErrors.length > 0) {
      setValidationErrors(allValidationErrors);
      Utils.scrollPageTop();
      return false;
    }

    return true;
  };

  const prepareFaultLogData = (formData: FaultLog): FaultLog | null => {
    const customerArrays = customerRowsToApi(customerRows);
    const deviceArrays = deviceRowsToApi(deviceRows);

    const mergedData: FaultLog = {
      ...data,
      ...formData,
      ...customerArrays,
      ...deviceArrays,
    };

    if (data?.status !== FaultLogStatus.Closed && formData.status === FaultLogStatus.Closed) {
      mergedData.closedDate = new Date();
    }

    if (mergedData.id === 0) {
      mergedData.reportedBy = userName!;
    }

    return mergedData;
  };

  const generateComments = (mergedData: FaultLog, isEngineerNotify: boolean = false, isLogisticsNotify: boolean = false) => {
    // Detect field changes
    const changeLog = data ? BusinessHelper.DetectFaultLogFieldChanges(data, mergedData) : [];
    if (isEngineerNotify) {
      changeLog.push("Sent engineering notification");
    }

    if (isLogisticsNotify) {
      changeLog.push("Sent logistics notification");
    }

    // Build comment: manual comment first, then change log
    let finalComment = "";
    if (newComments.trim()) {
      finalComment = newComments.trim();
    }
    if (changeLog.length > 0) {
      finalComment = finalComment ? `${finalComment}, ${changeLog.join(", ")}` : changeLog.join(", ");
    }

    // Save comment (even if only field changes, no manual comment)
    return finalComment ? [{ comments: finalComment } as FaultLogComment] : [];
  };

  const generateFaultLogForSubmit = (
    formData: FaultLog,
    isEngineerNotify: boolean = false,
    isLogisticsNotify: boolean = false
  ): FaultLog | null => {
    const mergedData = prepareFaultLogData(formData);
    if (!mergedData) return null;

    mergedData.comments = generateComments(mergedData, isEngineerNotify, isLogisticsNotify);
    return mergedData;
  };

  const handleFormSubmit = async (formData: FaultLog) => {
    if (!validateFaultLogData()) {
      return;
    }

    // check if user changed the form data
    const mergedData = prepareFaultLogData(formData);
    const changeLog = data && mergedData ? BusinessHelper.DetectFaultLogFieldChanges(data, mergedData) : [];
    // Close reminder must fire regardless of SAP/Engineering state or createdBy, so it's based on the status transition alone
    const isJustClosed = data?.status !== FaultLogStatus.Closed && mergedData?.status === FaultLogStatus.Closed;
    const canShowEngineerNotifyHint = changeLog.length > 0 && isJustClosed;
    await saveFaultLog(formData, canShowEngineerNotifyHint);
  };

  const saveFaultLog = async (formData: FaultLog, canShowEngineerNotifyHint: boolean) => {
    const postData = generateFaultLogForSubmit(formData);
    if (postData) {
      if (await handleSaveFaultLog(postData, canShowEngineerNotifyHint)) {
        setNewComments("");
        if (editItemId) {
          await loading(editItemId);
        }
        showSaveSuccessToast();
      }
    }
  };

  const handleSaveFaultLog = async (faultLog: FaultLog, canShowEngineerNotifyHint: boolean) => {
    if (faultLog.id === 0) {
      var newId = await FaultLogApi.addFaultLog(faultLog);
      if (newId !== 0) {
        setNewFaultLogId(newId);
        isFirstSaveNewDataRef.current = true;
        setNotifyHintMessage("Remember to send notification to logistics and/or engineering if required");
        setShowEngineerNotifyHint(true);
        return true;
      }

      return false;
    } else {
      const result = await FaultLogApi.updateFaultLog(faultLog);
      if (canShowEngineerNotifyHint) {
        setNotifyHintMessage("Remember to send notification to logistics if required");
        setShowEngineerNotifyHint(true);
      }
      return result;
    }
  };

  const showSaveSuccessToast = (message: string = "Fault details updated successfully.") => {
    store.dispatch(setToastSuccess(message));
    Utils.scrollPageTop();
  };

  // const handleEngineeringConfirmYes = async () => {
  //   setShowEngineeringConfirm(false);
  //   if (pendingFormData) {
  //     await handleSendEngineeringNotification(pendingFormData);
  //   }
  // };

  // const handleEngineeringConfirmNo = async () => {
  //   setShowEngineeringConfirm(false);
  //   if (pendingFormData) {
  //     await saveFaultLog(pendingFormData);
  //   }
  // };

  const handleSendEngineeringNotification = async (formData: FaultLog) => {
    if (!validateFaultLogData()) {
      return;
    }

    var postData = generateFaultLogForSubmit(formData, true, false);
    if (postData) {
      if (await FaultLogApi.updateFaultLogWithNotifySAP(postData)) {
        setIsEngineeringNotificationSend(true);
        store.dispatch(
          setToastSuccess(
            `Success to save fault log and create the send engineering notification to SAP. Please wait several seconds and refresh the page to view the SAP response.`
          )
        );
      }

      Utils.scrollPageTop();
    }
  };

  const handleSendLogisticsNotification = async (formData: FaultLog) => {
    if (!validateFaultLogData()) {
      return;
    }

    var postData = generateFaultLogForSubmit(formData, false, true);
    if (postData) {
      postData.isSendLogistics = true;
      postData.acknowledged = false;
      if (await FaultLogApi.updateFaultLog(postData)) {
        setIsLogisticsNotificationSend(true);
        store.dispatch(setToastSuccess(`Success to send logistics notification.`));
      }

      Utils.scrollPageTop();
    }
  };

  const handleOnRefresh = async () => {
    await loading(editItemId);
  };

  const handleFetchSapStatus = async () => {
    const workOrderId = (getValues("workOrderNumber") ?? "").trim();
    const sapNotifNum = getValues("sapNotificationNumber");
    if (!workOrderId && !sapNotifNum) return;
    setIsSyncingSapStatus(true);
    try {
      const sapData = await FaultLogApi.syncSapStatus(workOrderId, sapNotifNum);
      if (sapData) {
        if (sapData.workOrderNumber) setValue("workOrderNumber", sapData.workOrderNumber);
        if (sapData.sapNotificationNumber) setValue("sapNotificationNumber", parseInt(sapData.sapNotificationNumber, 10));

        setValue("workOrderLongText", sapData.workOrderLongText ?? "");
        setValue("workOrderUserStatus", sapData.workOrderUserStatus ?? "");
        setValue("workOrderSystemStatus", sapData.workOrderSystemStatus ?? "");
        setValue("workOrderUserStatusDesc", sapData.workOrderUserStatusDesc ?? "");
        setValue("workOrderSystemStatusDesc", sapData.workOrderSystemStatusDesc ?? "");
        setValue("notificationStatusDesc", sapData.notificationStatusDesc ?? "");
        setSapSyncToast({ show: true, message: "SAP data successfully synced." });
      } else {
        setSapSyncToast({ show: true, message: "SAP status fetched. No data returned." });
      }
    } finally {
      setIsSyncingSapStatus(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 1100, position: "fixed" }}>
        <Toast
          className="sap-sync-toast"
          show={sapSyncToast.show}
          onClose={() => setSapSyncToast({ show: false, message: "" })}
          delay={5000}
          autohide
        >
          <Toast.Body>
            <i className="fas fa-check-circle me-3 fa-lg"></i>
            {sapSyncToast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        {validationErrors.length > 0 && (
          <Alert variant="danger" dismissible onClose={() => setValidationErrors([])}>
            <Alert.Heading>Validation Errors</Alert.Heading>
            <ul className="mb-0">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Row>
          <SectionBorder lg={6}>
            <Row>
              <Col md={12}>
                <h5>Fault Location</h5>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{Constants.Label_ID}</Form.Label>
                  <Form.Control type="number" {...register("id")} readOnly className="readonly" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  label={Constants.Label_Priority}
                  required={true}
                  options={FaultLogPriorityList}
                  register={register("priority")}
                  disabled={isReadonly}
                  showUnknownValue={true}
                />
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  label={Constants.Label_Region}
                  required={true}
                  options={regionOptions}
                  register={register("region")}
                  disabled={isReadonly}
                  onChange={(e) => setCurrentSelectRegion(e.target.value)}
                  value={watch("region")}
                  showUnknownValue={true}
                />
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  label={Constants.Label_Country}
                  required={true}
                  options={countryOptions}
                  register={register("country")}
                  disabled={isReadonly}
                  value={watch("country")}
                  showUnknownValue={true}
                />
              </Col>
            </Row>
          </SectionBorder>

          <SectionBorder lg={6}>
            <Row>
              <Col md={12}>
                <h5>Fault Status</h5>
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  required={true}
                  label={Constants.Label_FaultCode}
                  options={faultCodesCodeList}
                  register={register("faultCode")}
                  disabled={isReadonly}
                  showUnknownValue={true}
                  value={watch("faultCode")}
                  onChange={(e) =>
                    setValue("workOrderInstructions", getFaultCodeWorkOrderInstructions(e.target.value), { shouldDirty: true })
                  }
                />
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  required={true}
                  label={Constants.Label_Owner}
                  options={FaultLogOwnerList}
                  showUnknownValue={true}
                  register={register("owner")}
                  disabled={isReadonly}
                />
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  label={Constants.Label_Status}
                  required={true}
                  options={FaultLogStatusList}
                  register={register("status")}
                  disabled={isReadonly}
                  showUnknownValue={true}
                />
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  required={true}
                  label={Constants.Label_ImpactOnLogistics}
                  options={FaultLogLogisticsImpactList}
                  register={register("impactOnLogistics")}
                  disabled={isReadonly}
                  value={watch("impactOnLogistics")}
                  showUnknownValue={true}
                />
              </Col>
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          <SectionBorder lg={12} className="h-100">
            <Row>
              <Col md={12}>
                <h5>Report Detail</h5>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_ReportedTime}</Form.Label>
                  <Form.Control
                    type="text"
                    value={DateTimeHelper.ToLocalString(data?.reportedTime)}
                    readOnly
                    className="readonly"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_ReportedBy}</Form.Label>
                  <Form.Control type="text" {...register("reportedBy")} readOnly className="readonly" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_ToEngineeringDate}</Form.Label>
                  <Form.Control
                    type="text"
                    value={DateTimeHelper.ToLocalString(data?.toEngineeringDate)}
                    readOnly
                    className="readonly"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_AcknowledgedTime}</Form.Label>
                  <Form.Control
                    type="text"
                    value={DateTimeHelper.ToLocalString(data?.acknowledgedTime)}
                    readOnly
                    className="readonly"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_AcknowledgedBy}</Form.Label>
                  <Form.Control type="text" {...register("acknowledgedBy")} readOnly className="readonly" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_ClosedDate}</Form.Label>
                  <Form.Control
                    type="text"
                    value={data?.closedDate ? DateTimeHelper.ToLocalString(data.closedDate) : ""}
                    readOnly
                    className="readonly"
                  />
                </Form.Group>
              </Col>
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          <SectionBorder lg={12}>
            <Row>
              <Col md={12} className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">SAP Information</h5>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  disabled={isSyncingSapStatus || (!watch("workOrderNumber") && !watch("sapNotificationNumber"))}
                  onClick={handleFetchSapStatus}
                >
                  {isSyncingSapStatus ? "Fetching..." : "Fetch SAP Status"}
                </Button>
              </Col>
              <Col md={3}>
                <FormGroupNumber
                  label={Constants.Label_SapNotificationNumber}
                  register={register("sapNotificationNumber")}
                  maxLength={12}
                  allowDecimal={false}
                  readOnly={isReadonly}
                  className={isReadonly ? "readonly" : ""}
                />
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_RootCause}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("rootCause")}
                    maxLength={100}
                    readOnly={isReadonly}
                    className={isReadonly ? "readonly" : ""}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_WorkOrderNumber}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("workOrderNumber")}
                    maxLength={50}
                    readOnly={isReadonly}
                    className={isReadonly ? "readonly" : ""}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_SiteVisitDate}</Form.Label>
                  <Form.Control
                    type="text"
                    value={DateTimeHelper.ToLocalString(data?.siteVisitDate)}
                    readOnly
                    className="readonly"
                  />
                </Form.Group>
              </Col>
              {/* <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_WorkOrderUserStatus}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("workOrderUserStatus")}
                    maxLength={50}
                    readOnly={true}
                    className="readonly"
                  />
                </Form.Group>
              </Col> */}
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_NotificationStatus}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("notificationStatusDesc")}
                    readOnly={true}
                    className="readonly"
                    title={watch("notificationStatusDesc") || ""}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_WorkOrderUserStatusDesc}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("workOrderUserStatusDesc")}
                    readOnly={true}
                    className="readonly"
                    title={watch("workOrderUserStatusDesc") || ""}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_WorkOrderSystemStatusDesc}</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("workOrderSystemStatusDesc")}
                    readOnly={true}
                    className="readonly"
                    title={watch("workOrderSystemStatusDesc") || ""}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-2">
                  <Form.Label>{Constants.Label_WorkOrderLongText}</Form.Label>
                  <Form.Control as="textarea" rows={6} {...register("workOrderLongText")} readOnly={true} className="readonly" />
                </Form.Group>
              </Col>
              {data?.lastCreateSapNotifyMessage && (
                <Col md={12}>
                  <Form.Group className="mb-2">
                    <Form.Label className="text-danger">{Constants.Label_SapNotifyResponseMessage}</Form.Label>
                    <Form.Control
                      type="text"
                      value={data?.lastCreateSapNotifyMessage}
                      readOnly
                      className="readonly text-danger"
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          <SectionBorder lg={12}>
            <Row>
              <Col md={12}>
                <h5>Fault Asset & Device</h5>
              </Col>
              <Col md={6}>
                <FormGroupSelect
                  label={Constants.Label_TelemetrySystem}
                  required={true}
                  options={FaultLogTelemetrySystemList}
                  register={register("telemetrySystem")}
                  disabled={isReadonly}
                  value={watch("telemetrySystem")}
                  showUnknownValue={true}
                />
              </Col>
              <Col md={6}>
                <FormGroupMultiInput
                  label={Constants.Label_AssetName}
                  register={register("assetName")}
                  disabled={isReadonly}
                ></FormGroupMultiInput>
              </Col>

              <Col md={12}>
                <EditableTable
                  rows={deviceRows}
                  columns={[
                    { key: "id", label: Constants.Label_DeviceID, required: true, maxLength: 50, placeholder: "Enter device ID" },
                    {
                      key: "type",
                      label: Constants.Label_DeviceType,
                      required: true,
                      maxLength: 50,
                      placeholder: "Enter device type",
                    },
                  ]}
                  onChange={setDeviceRows}
                  createEmptyRow={createEmptyDeviceRow}
                  isReadonly={isReadonly}
                  label="Devices"
                />
              </Col>
              <Col md={12}>
                <DevicePicList fileNames={devicePicFileNames} />
              </Col>
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          <SectionBorder lg={12}>
            <Row>
              <Col md={12}>
                <h5>Customer Information</h5>
              </Col>
              <Col md={12}>
                <EditableTable
                  rows={customerRows}
                  columns={customerInfoColumns}
                  onChange={setCustomerRows}
                  createEmptyRow={createEmptyCustomerRow}
                  isReadonly={isReadonly}
                  label="Customers"
                  required={true}
                  scrollable={true}
                />
              </Col>
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          <SectionBorder lg={12}>
            <Row className="mb-2">
              <Col md={12}>
                <h5>Comment</h5>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Current Fault Comments</Form.Label>
                  <Form.Control as="textarea" rows={6} {...register("combinedComments")} readOnly className="readonly" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Add New Fault Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={newComments}
                    onChange={(e) => setNewComments(e.target.value)}
                    readOnly={isReadonly}
                    className={isReadonly ? "readonly" : ""}
                    placeholder="Optional: Add additional comments (field changes are tracked automatically)"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Work Order Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    {...register("workOrderInstructions")}
                    maxLength={1000}
                    readOnly={isReadonly}
                    className={isReadonly ? "readonly" : ""}
                  />
                </Form.Group>
              </Col>
            </Row>
          </SectionBorder>
        </Row>

        <Row>
          {editItemId && (canSendEngineeringNotification || canSendLogisticsNotification) ? (
            <Col className="d-flex gap-2">
              {canSendEngineeringNotification && !isCustomerManagedDevice && (
                <Button
                  variant="primary"
                  disabled={isSendEngineeringNotificationDisabled}
                  onClick={handleSubmit(handleSendEngineeringNotification)}
                >
                  Send Engineering Notification
                </Button>
              )}

              {canSendLogisticsNotification && (
                <Button
                  variant="primary"
                  disabled={data?.isSendLogistics || isLogisticsNotificationSend}
                  onClick={handleSubmit(handleSendLogisticsNotification)}
                >
                  Send Logistics Notification
                </Button>
              )}
            </Col>
          ) : null}

          <Col className="d-flex justify-content-end gap-2">
            {!isReadonly && data && (
              <Button variant="primary" type="submit">
                Save
              </Button>
            )}
            {editItemId ? (
              <Button variant="primary" onClick={handleOnRefresh}>
                Refresh
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </Col>
        </Row>

        {/* <ApModalDialog
          props={{
            show: showEngineeringConfirm,
            onHide: handleEngineeringConfirmNo,
            backdrop: "static",
          }}
          onOkay={handleEngineeringConfirmYes}
          header={true}
          headerText="Send Engineering Notification"
          footer={true}
          okText="Yes"
          closeText="No"
        >
          Do you want to send an engineering notification? <br />
          Click Yes to save fault log and send notification <br />
          Click No to save fault log without sending notification
        </ApModalDialog> */}

        <ApModalDialog
          props={{
            show: showEngineerNotifyHint,
            onHide: () => setShowEngineerNotifyHint(false),
            backdrop: "static",
          }}
          header={true}
          headerText="Message"
          footer={true}
          closeText="Ok"
        >
          {notifyHintMessage}
        </ApModalDialog>
      </Form>
    </FormProvider>
  );
};

export default FaultLogEditForm;
